import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FolderTree, Lock, Plus, Trash2, AlertTriangle } from 'lucide-react';
import {
  useGetClaimProjectGroups, usePutClaimProjectGroups, getGetClaimProjectGroupsQueryKey, type ClaimProjectGroupsView,
} from '@edison/api-client-react';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';

const errMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

type Draft = { mainProjectId: string; memberProjectIds: string[] };

const toDraft = (view: ClaimProjectGroupsView): Draft[] =>
  view.groups.map((g) => ({ mainProjectId: g.mainProjectId, memberProjectIds: [...g.members.map((m) => m.projectId)].sort() }));
const same = (a: Draft[], b: Draft[]) => JSON.stringify(a) === JSON.stringify(b);

interface Props { claimId: string }

/**
 * Claim project groups (spec 2026-10-06): pick a main project, tick the projects that roll into
 * it. On the timesheet the members appear under the main project's name and claim activity,
 * priced at their OWN adopted share — nothing changes on the projects, shares or figures.
 * A locked claim shows its frozen snapshot read-only.
 */
export function ClaimProjectGroupsSection({ claimId }: Props) {
  const qc = useQueryClient();
  const query = useGetClaimProjectGroups(claimId);
  const put = usePutClaimProjectGroups();
  const view = query.data?.data as ClaimProjectGroupsView | undefined;
  const [draft, setDraft] = useState<Draft[] | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  // Re-sync the draft from the server only while it is untouched (or right after a save), so a
  // background refetch never clobbers an edit in progress.
  const syncedRef = useRef<Draft[]>([]);
  const resyncRef = useRef(false);
  useEffect(() => {
    if (!view) return;
    const next = toDraft(view);
    const prev = syncedRef.current;
    const force = resyncRef.current;
    syncedRef.current = next;
    resyncRef.current = false;
    setDraft((cur) => (cur === null || force || same(cur, prev) ? next : cur));
  }, [view]);

  // Every project the section can name: grouped ones (from the view) + ungrouped claimable ones.
  const projectNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of view?.groups ?? []) { m.set(g.mainProjectId, g.mainProjectName); for (const mem of g.members) m.set(mem.projectId, mem.name); }
    for (const p of view?.ungrouped ?? []) m.set(p.projectId, p.name);
    return m;
  }, [view]);
  // Claimable: ungrouped entries, mains (the view carries no flag for them) and members flagged claimable.
  const claimableIds = useMemo(() => {
    const s = new Set<string>();
    for (const g of view?.groups ?? []) { s.add(g.mainProjectId); for (const mem of g.members) if (mem.claimable) s.add(mem.projectId); }
    for (const p of view?.ungrouped ?? []) s.add(p.projectId);
    return s;
  }, [view]);
  const allProjects = useMemo(() => [...projectNames].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)), [projectNames]);

  if (query.isLoading || (view && !draft)) return <SkeletonCard />;
  if (query.error || !view || !draft) return <InlineError message="Failed to load the project groups." onRetry={() => void query.refetch()} />;

  const locked = view.locked;
  const dirty = !same(draft, toDraft(view));
  const usedBy = (projectId: string, exceptIdx: number): number | null => {
    const i = draft.findIndex((g, idx) => idx !== exceptIdx && (g.mainProjectId === projectId || g.memberProjectIds.includes(projectId)));
    return i === -1 ? null : i;
  };
  const update = (idx: number, patch: Partial<Draft>) => { setSaved(false); setDraft(draft.map((g, i) => (i === idx ? { ...g, ...patch } : g))); };
  const save = () => {
    setError('');
    setSaved(false);
    put.mutate({ id: claimId, data: { groups: draft.filter((g) => g.mainProjectId) } }, {
      onSuccess: () => { setSaved(true); resyncRef.current = true; void qc.invalidateQueries({ queryKey: getGetClaimProjectGroupsQueryKey(claimId) }); },
      onError: (err: unknown) => setError(errMsg(err, 'Could not save the project groups.')),
    });
  };
  const grouped = draft.reduce((n, g) => n + (g.mainProjectId ? 1 + g.memberProjectIds.length : 0), 0);
  const ungrouped = allProjects.filter((p) => claimableIds.has(p.id) && usedBy(p.id, -1) === null).length;
  const incomplete = draft.some((g) => !g.mainProjectId || g.memberProjectIds.length === 0);
  // A non-claimable project may only stay listed in the card it currently belongs to.
  const ownNonClaimable = (g: Draft): Set<string> => {
    const s = new Set(g.memberProjectIds.filter((id) => !claimableIds.has(id)));
    for (const vg of view.groups) if (vg.mainProjectId === g.mainProjectId) for (const m of vg.members) if (!m.claimable) s.add(m.projectId);
    return s;
  };
  const activityOf = (projectId: string) => view.mappingByProject[projectId] ?? [];

  return (
    <Card variant="static" className="p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium flex items-center gap-2"><FolderTree className="h-4 w-4" aria-hidden="true" />Project groups</h3>
        {locked ? <Badge variant="default"><Lock className="h-3 w-3 mr-1 inline" aria-hidden="true" />Frozen at lock</Badge>
          : <Button variant="secondary" size="sm" onClick={() => { setSaved(false); setDraft([...draft, { mainProjectId: '', memberProjectIds: [] }]); }}><Plus className="h-4 w-4 mr-1" aria-hidden="true" />Add group</Button>}
      </div>
      <p className="text-xs text-muted-foreground">Members appear on the timesheet under their main project, priced at their own R&D share. Nothing changes on the projects themselves.</p>

      {draft.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No groups yet. Add a group to roll projects into one timesheet line.</p>
      ) : draft.map((g, idx) => {
        const mainActs = g.mainProjectId ? activityOf(g.mainProjectId) : [];
        return (
          <div key={idx} data-testid={`project-group-${idx}`} className="rounded-md border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <label htmlFor={`main-${idx}`} className="text-xs text-muted-foreground w-24">Main project</label>
              <select id={`main-${idx}`} className="flex-1 rounded-md border bg-background px-2 py-1 text-sm" disabled={locked} value={g.mainProjectId}
                onChange={(e) => update(idx, { mainProjectId: e.target.value, memberProjectIds: g.memberProjectIds.filter((m) => m !== e.target.value) })}>
                <option value="">Choose a project</option>
                {allProjects.filter((p) => claimableIds.has(p.id)).map((p) => <option key={p.id} value={p.id} disabled={usedBy(p.id, idx) !== null || g.memberProjectIds.includes(p.id)}>{p.name}</option>)}
              </select>
              {!locked && <Button variant="ghost" size="sm" aria-label="Remove group" onClick={() => { setSaved(false); setDraft(draft.filter((_, i) => i !== idx)); }}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>}
            </div>
            <fieldset className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
              <legend className="text-xs text-muted-foreground mb-1">Members</legend>
              {allProjects.filter((p) => p.id !== g.mainProjectId && (claimableIds.has(p.id) || ownNonClaimable(g).has(p.id))).map((p) => {
                const elsewhere = usedBy(p.id, idx) !== null;
                const checked = g.memberProjectIds.includes(p.id);
                const acts = activityOf(p.id);
                const differs = checked && mainActs.length > 0 && acts.length > 0 && JSON.stringify(acts) !== JSON.stringify(mainActs);
                return (
                  <div key={p.id} className="text-sm">
                    <label className={`flex items-center gap-2 ${elsewhere ? 'text-muted-foreground' : ''}`}>
                      <input type="checkbox" disabled={locked || elsewhere} checked={checked}
                        onChange={(e) => update(idx, { memberProjectIds: (e.target.checked ? [...g.memberProjectIds, p.id] : g.memberProjectIds.filter((m) => m !== p.id)).sort() })} />
                      {p.name}
                    </label>
                    {!claimableIds.has(p.id) && <p className="text-xs text-muted-foreground ml-6">no longer claimable</p>}
                    {differs && (
                      <p className="text-xs text-muted-foreground ml-6 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" aria-hidden="true" />
                        mapped to Activity {acts.join('/')}; keeps its own Activity {acts.join('/')} lines under {projectNames.get(g.mainProjectId) ?? 'the main project'} on the timesheet</p>
                    )}
                  </div>
                );
              })}
            </fieldset>
          </div>
        );
      })}

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{draft.filter((g) => g.mainProjectId).length} {draft.filter((g) => g.mainProjectId).length === 1 ? 'group' : 'groups'}; {grouped} projects shown under a main project; {ungrouped} ungrouped.</p>
        <div className="flex items-center gap-2">
          {!locked && saved && <p role="status" className="text-xs text-muted-foreground">Saved.</p>}
          {!locked && incomplete && <p className="text-xs text-destructive">Choose a main project and at least one member for each group.</p>}
          <Button size="sm" disabled={locked || !dirty || incomplete || put.isPending} onClick={save}>Save</Button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-destructive flex items-start gap-1"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />{error}</p>}
    </Card>
  );
}
